import { useState } from 'react';
import {
  SendOtpRequest,
  ValidateOtpRequest,
  ValidateOtpResponse,
  ValidateResetPasswordOtpRequest
} from '../_types/otp.types';
import { notification } from 'components/ds';
import { useOtpStorage } from './useOtpStorage';
import { useAuthStorage } from './useAuthStorage';
import { useOtpService } from '../services/useOtp.service';
import { useParticipantId } from './useParticipantId';

interface UseOtpReturn {
  sendOtpRequest: (data: SendOtpRequest) => Promise<boolean>;
  validateOtpCode: (data: ValidateOtpRequest) => Promise<ValidateOtpResponse | null>;
  validateOtpOnForgotPassword: (data: ValidateResetPasswordOtpRequest) => Promise<boolean>;
  loading: boolean;
  error: string | null;
  otpId: string | null;
  otpCode: string | null;
}

export const useOtp = (): UseOtpReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {otpId, setOtpId, otpCode, setOtpCode} = useOtpStorage();
  const {saveToken} = useAuthStorage()
  const {sendOtp, validateOtp, validateOtpForgotPasswordCode} = useOtpService()
  const {participantId} = useParticipantId();
  
  const sendOtpRequest = async (data: SendOtpRequest): Promise<boolean> => {
    
    const getVerificationMessage = (channel: string) => {
      if (channel === 'email') {
        return 'Por favor, verifique seu e-mail para obter o código.';
      } else if (channel === 'sms') {
        return 'Por favor, verifique seu celular para obter o código.';
      }
      
      return `Por favor, verifique seu ${channel || 'dispositivo'} para obter o código.`;
    };
    
    if (loading) return false;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await sendOtp(data);
      
      if (response) {
        setOtpId(response.otpId);
        
        notification.success({
          message: 'Código enviado com sucesso',
          description: getVerificationMessage(data?.channel || '')
        });
        
        return true;
      }
      
      notification.error({
        message: 'Erro ao enviar código',
        description: 'Não foi possível enviar o código. Por favor, tente novamente.'
      });
      
      return false;
      
    } catch (err: any) {
      console.error('Erro no sendOtpRequest:', err);
      setError(err?.response?.data?.message || 'Erro ao enviar solicitação de código OTP');
      notification.error({
        message: 'Erro ao enviar código',
        description: 'Não foi possível enviar o código. Por favor, tente novamente.'
      });
      
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  const validateOtpCode = async (data: ValidateOtpRequest): Promise<ValidateOtpResponse | null> => {
    if (loading) return null;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await validateOtp(data);
      
      if (response.validCode && response.accessToken) {
        setOtpCode(data.code);
        saveToken(response.accessToken);
        
        return response;
      }
      
      notification.error({
        message: 'Código inválido',
        description: 'O código informado é inválido. Por favor, tente novamente.'
      });
      
      return null;
    } catch (error: any) {
      console.error('Erro no validateOtpCode:', error);
      setError(error?.response?.data?.message || 'Erro ao validar código OTP');
      notification.error({
        message: 'Erro na validação',
        description: 'Ocorreu um erro ao validar o código. Por favor, tente novamente.'
      });
      
      return null;
    } finally {
      setLoading(false);
    }
  };
  
  const validateOtpOnForgotPassword = async (data: ValidateResetPasswordOtpRequest): Promise<boolean> => {
    setOtpCode(data.code);
    
    if (loading) return false;
    
    try {
      setLoading(true);
      setError(null);
      
      const validationData = {
        otpId: data.otpId,
        code: data.code,
        participantId: participantId || data.participantId
      };
      
      const response = await validateOtpForgotPasswordCode(validationData);
      
      if (response) {
        saveToken(response?.accessToken);
        
        notification.success({
          message: 'Código validado',
          description: 'Código validado com sucesso!'
        });
        
        return true;
      }
      
      notification.error({
        message: 'Erro na validação',
        description: 'A resposta do servidor não contém os dados esperados.'
      });
      
      return true;
      
    } catch (err: any) {
      
      setError(err?.response?.data?.message || 'Erro ao validar código');
      
      notification.error({
        message: 'Código inválido',
        description: 'O código informado é inválido. Por favor, tente novamente.'
      });
      
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  return {
    sendOtpRequest,
    validateOtpCode,
    validateOtpOnForgotPassword,
    loading,
    error,
    otpId,
    otpCode
  };
};
